import type { AdminCommand } from '@thoughtspot/admin';
import {
    buildCommandRegistry,
    getSideBarConfig,
    useGetStatusLazy,
} from '@thoughtspot/admin';
import { isOrgsOnFTEnabled } from '@thoughtspot/feature-service';
import {
    SpotlightItem,
    SpotlightItemType,
} from '@thoughtspot/global-nav';
import { useTranslation } from '@thoughtspot/i18n';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import {
    getCurrentOrgId,
    hasPrivilege,
    isAdminTabVisible,
    isComsEnabled,
    isCopilotEnabled,
    isCustomChartEnabled,
    isCustomizeEmailFromAdminEnabled,
    isCustomRegionEnabled,
    isModularHomeExperienceEnabled,
    isOrgUiEnabled,
    isReactshellEnabled,
    isRolesEnabled,
    isSaaSCluster,
    isVariablesUIEnabled,
    PRIVILEGES,
} from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthenticatedState } from '/@services/system/login-service';

/** Shape of data.Data returned by useGetStatusLazy — derived from the hook's return type. */
type AdminStatusData = NonNullable<ReturnType<typeof useGetStatusLazy>['data']['Data']>;

/** Matches the `strings` alias used by getSideBarConfig: (x: string) => string */
type TranslateFn = (key: string) => string;

const ADMIN_ITEM_PREFIX = 'admin-cmd-';

const CONTEXT_ALL_ID = 'all-org';
const CONTEXT_PRIMARY_ID = 'primary-org';

const STYLE_CUSTOMIZATION_CMD_ID = 'settings-style-customization-react';
const THOUGHTSPOT_AI_CMD_ID = 'tscli-ui-sage_feature';

const THOUGHTSPOT_AI_SUB_ITEMS = [
    {
        id: 'ts-ai-spotter-version',
        labelKey: 'spotterExperience.title',
        keywords: ['spotter', 'version', 'ai', 'model'],
    },
    {
        id: 'ts-ai-activation-targets',
        labelKey: 'spotterActivationTargets.title',
        keywords: ['spotter', 'access', 'users', 'activation'],
    },
    {
        id: 'ts-ai-spotter3-capabilities',
        labelKey: 'spotter3Capabilities.title',
        keywords: ['spotter', 'capabilities', 'features', 'ai'],
    },
    {
        id: 'ts-ai-user-experience',
        labelKey: 'configureSpotterUserExperience.title',
        keywords: ['spotter', 'user', 'experience', 'configure'],
    },
    {
        id: 'ts-ai-viz-settings',
        labelKey: 'spotterVizSettings.title',
        keywords: ['spotter', 'viz', 'visualization', 'settings'],
    },
    {
        id: 'ts-ai-spotter-model',
        labelKey: 'spotterModel.title',
        keywords: ['spotter', 'model', 'ai'],
    },
    {
        id: 'ts-ai-additional-features',
        labelKey: 'additionalAiFeatures.title',
        keywords: ['ai', 'features', 'additional', 'spotter'],
    },
    {
        id: 'ts-ai-llm-connections',
        labelKey: 'aiModelType.title',
        keywords: ['llm', 'model', 'connections', 'ai'],
    },
] as const;

const SEARCH_SPOTIQ_CMD_ID = 'tscli-ui-search';
const ADMINISTRATION_CMD_ID = 'tscli-ui-administration';

const SEARCH_SPOTIQ_SUB_ITEMS = [
    {
        id: 'search-index-settings',
        labelKey: 'indexSetting.title',
        keywords: ['index', 'search', 'indexing', 'settings'],
    },
    {
        id: 'search-formula-settings',
        labelKey: 'formulaSetting.title',
        keywords: ['formula', 'search', 'settings', 'passthrough'],
    },
    {
        id: 'search-query-settings',
        labelKey: 'querySetting.title',
        keywords: ['query', 'search', 'settings', 'columns'],
    },
    {
        id: 'search-spotiq',
        labelKey: 'spotiq.title',
        keywords: ['spotiq', 'ai', 'insights', 'analysis', 'search'],
    },
] as const;

const ADMINISTRATION_SUB_ITEMS = [
    {
        id: 'admin-org-switcher',
        labelKey: 'showOrgSwitcherToUsersWithMultipleOrgs.title',
        keywords: ['org', 'switcher', 'users', 'multiple', 'administration'],
    },
    {
        id: 'admin-org-url-sharing',
        labelKey: 'orgPerUrlSharing.title',
        keywords: ['org', 'url', 'sharing', 'administration'],
    },
    {
        id: 'admin-selective-access',
        labelKey: 'enableSelectiveUserAccessRestriction.title',
        keywords: ['selective', 'user', 'access', 'restriction', 'administration'],
    },
    {
        id: 'admin-intercom',
        labelKey: 'intercomEnabled.title',
        keywords: ['intercom', 'support', 'in-app', 'administration'],
    },
] as const;

const STYLE_CUSTOMIZATION_SUB_ITEMS = [
    {
        id: 'style-logo-default',
        labelKey: 'spotlight.nav.styleLogoDefault',
        keywords: ['logo', 'favicon', 'branding', 'style', 'application'],
    },
    {
        id: 'style-logo-wide',
        labelKey: 'spotlight.nav.styleLogoWide',
        keywords: ['logo', 'wide', 'branding', 'style', 'application'],
    },
    {
        id: 'style-chart-fonts',
        labelKey: 'spotlight.nav.styleChartFonts',
        keywords: ['font', 'chart', 'visualization', 'typography', 'style'],
    },
    {
        id: 'style-table-fonts',
        labelKey: 'spotlight.nav.styleTableFonts',
        keywords: ['font', 'table', 'visualization', 'typography', 'style'],
    },
    {
        id: 'style-nav-color',
        labelKey: 'spotlight.nav.styleNavColor',
        keywords: ['navigation', 'panel', 'color', 'style', 'customize'],
    },
    {
        id: 'style-chart-colors',
        labelKey: 'spotlight.nav.styleChartColors',
        keywords: ['chart', 'color', 'palette', 'style', 'customize'],
    },
    {
        id: 'style-footer-text',
        labelKey: 'spotlight.nav.styleFooterText',
        keywords: ['footer', 'text', 'embed', 'embedded', 'style'],
    },
] as const;

const HOMEPAGE_DISCOVERY_CMD_ID = 'homepage-and-discovery';
const CHART_CUSTOMIZATION_CMD_ID = 'chart-customization';

const HOMEPAGE_DISCOVERY_SUB_ITEMS = [
    {
        id: 'homepage-alert-banner',
        labelKey: 'admin.home.announcement.config.title',
        keywords: ['alert', 'banner', 'announcement', 'homepage', 'custom'],
    },
    {
        id: 'homepage-customize',
        labelKey: 'homepage.modular.customize.button',
        keywords: ['customize', 'homepage', 'modules', 'home'],
    },
    {
        id: 'homepage-auto-translate',
        labelKey: 'adminSection.autoTranslation.title',
        keywords: ['translate', 'auto', 'liveboards', 'answers', 'language'],
    },
] as const;

const CHART_CUSTOMIZATION_SUB_ITEMS = [
    {
        id: 'chart-custom-maps',
        labelKey: 'adminSection.chartCustomization.geomaps.title',
        keywords: ['maps', 'geo', 'custom', 'chart', 'geography'],
    },
    {
        id: 'chart-custom-charts',
        labelKey: 'adminSection.chartCustomization.customCharts.title',
        keywords: ['charts', 'custom', 'byoc', 'visualization'],
    },
] as const;

const adminCommandMeta = new WeakMap<
    SpotlightItem,
    { commandId: string; sectionId?: string; orgContext?: string }
>();

const joinPath = (...parts: (string | undefined)[]) =>
    parts.filter(Boolean).join(' / ');

function hasCommandWithId(commands: AdminCommand[], id: string): boolean {
    return commands.some(cmd => cmd.id === id || cmd.parentId === id);
}


function buildSubItems<
    T extends readonly { id: string; labelKey: string; keywords: readonly string[] }[]
>(
    items: T,
    cmdId: string,
    sectionLabelKey: string,
    t: TranslateFn,
    contextLabel: string | undefined,
    orgContext: string | undefined,
): SpotlightItem[] {
    const baseDescription = joinPath(
        t('adminSection.application_settings.name'),
        t(sectionLabelKey),
    );
    const description = contextLabel
        ? joinPath(contextLabel, baseDescription)
        : baseDescription;

    return items.map(({ id, labelKey, keywords }) => {
        const item: SpotlightItem = {
            id: `${ADMIN_ITEM_PREFIX}${id}${orgContext ? `-${orgContext}` : ''}`,
            label: t(labelKey),
            description,
            icon: IconID.COG,
            type: SpotlightItemType.ADMIN,
            keywords: [...keywords],
        };
        adminCommandMeta.set(item, { commandId: cmdId, orgContext });
        return item;
    });
}

function buildStyleCustomizationItems(
    t: TranslateFn,
    contextLabel: string | undefined,
    orgContext: string | undefined,
): SpotlightItem[] {
    return buildSubItems(
        STYLE_CUSTOMIZATION_SUB_ITEMS,
        STYLE_CUSTOMIZATION_CMD_ID,
        'adminSection.styleCustomization.tabHeader',
        t,
        contextLabel,
        orgContext,
    );
}

function buildHomepageDiscoveryItems(
    t: TranslateFn,
    contextLabel: string | undefined,
    orgContext: string | undefined,
): SpotlightItem[] {
    return buildSubItems(
        HOMEPAGE_DISCOVERY_SUB_ITEMS,
        HOMEPAGE_DISCOVERY_CMD_ID,
        'adminSection.homePageAppExperience.title',
        t,
        contextLabel,
        orgContext,
    );
}

function buildChartCustomizationItems(
    t: TranslateFn,
    contextLabel: string | undefined,
    orgContext: string | undefined,
): SpotlightItem[] {
    return buildSubItems(
        CHART_CUSTOMIZATION_SUB_ITEMS,
        CHART_CUSTOMIZATION_CMD_ID,
        'adminSection.chartCustomization.title',
        t,
        contextLabel,
        orgContext,
    );
}

function buildThoughtSpotAIItems(
    t: TranslateFn,
    contextLabel: string | undefined,
    orgContext: string | undefined,
): SpotlightItem[] {
    const baseDescription = joinPath(
        t('adminSection.application_settings.name'),
        t('tscli.config.sage_feature.title'),
    );
    const description = contextLabel
        ? joinPath(contextLabel, baseDescription)
        : baseDescription;

    return THOUGHTSPOT_AI_SUB_ITEMS.map(({ id, labelKey, keywords }) => {
        const item: SpotlightItem = {
            id: `${ADMIN_ITEM_PREFIX}${id}${orgContext ? `-${orgContext}` : ''}`,
            label: t(labelKey),
            description,
            icon: IconID.COG,
            type: SpotlightItemType.ADMIN,
            keywords: [...keywords],
        };
        adminCommandMeta.set(item, {
            commandId: THOUGHTSPOT_AI_CMD_ID,
            orgContext,
        });
        return item;
    });
}

function buildSearchSpotIQItems(
    t: TranslateFn,
    contextLabel: string | undefined,
    orgContext: string | undefined,
): SpotlightItem[] {
    return buildSubItems(
        SEARCH_SPOTIQ_SUB_ITEMS,
        SEARCH_SPOTIQ_CMD_ID,
        'tscli.config.search.title',
        t,
        contextLabel,
        orgContext,
    );
}

function buildAdministrationItems(
    t: TranslateFn,
    contextLabel: string | undefined,
    orgContext: string | undefined,
): SpotlightItem[] {
    const baseDescription = joinPath(
        t('adminSection.application_settings.name'),
        t('adminSection.administration.name'),
    );
    const description = contextLabel
        ? joinPath(contextLabel, baseDescription)
        : baseDescription;

    return ADMINISTRATION_SUB_ITEMS.map(({ id, labelKey, keywords }) => {
        const item: SpotlightItem = {
            id: `${ADMIN_ITEM_PREFIX}${id}${orgContext ? `-${orgContext}` : ''}`,
            label: t(labelKey),
            description,
            icon: IconID.COG,
            type: SpotlightItemType.ADMIN,
            keywords: [...keywords],
        };
        adminCommandMeta.set(item, {
            commandId: ADMINISTRATION_CMD_ID,
            orgContext,
        });
        return item;
    });
}

function getDefaultStatusConfig(contextId?: string): AdminStatusData {
    const orgId = getCurrentOrgId();
    const orgsEnabled =
        isOrgUiEnabled() && (orgId === -1 || orgId === 0);

    let resolvedContextId = contextId;
    if (resolvedContextId === undefined) {
        if (orgId === -1) {
            resolvedContextId = CONTEXT_ALL_ID;
        } else if (orgId === 0) {
            resolvedContextId = CONTEXT_PRIMARY_ID;
        } else {
            resolvedContextId = '';
        }
    }

    let isSwitchOrgTabVisible = false;
    if (orgsEnabled) {
        if (isRolesEnabled()) {
            isSwitchOrgTabVisible =
                hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                (hasPrivilege(PRIVILEGES.USER_ADMINISTRATION) ||
                    hasPrivilege(PRIVILEGES.GROUP_ADMINISTRATION) ||
                    hasPrivilege(PRIVILEGES.ROLE_ADMINISTRATION));
        } else {
            isSwitchOrgTabVisible = true;
        }
    }

    if (isSwitchOrgTabVisible) {
        if (resolvedContextId === CONTEXT_ALL_ID) {
            return {
                status: {
                    org_management: { org: true },
                    user_management: { users: true },
                    authentication: { saml: true },
                    application_settings: {
                        homepage_and_discovery: isModularHomeExperienceEnabled(),
                        email_customizations:
                            isComsEnabled() &&
                            isCustomizeEmailFromAdminEnabled(),
                        variables:
                            hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                            isVariablesUIEnabled(),
                    },
                },
                __typename: 'Admin__Data' as const,
            };
        } 
            return {
                status: {
                    user_management: { users: true, groups: true, roles: true },
                    application_settings: {
                        chart_customization:
                            isCustomChartEnabled() || isCustomRegionEnabled(),
                        homepage_and_discovery: isModularHomeExperienceEnabled(),
                        email_customizations:
                            isComsEnabled() &&
                            isCustomizeEmailFromAdminEnabled(),
                    },
                },
                __typename: 'Admin__Data' as const,
            };
        
    } if (orgsEnabled) {
        return {
            status: {
                user_management: { users: true, groups: true, roles: true },
                org_management: hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION)
                    ? { org: true }
                    : { org: false },
                application_settings: {
                    chart_customization:
                        isCustomChartEnabled() || isCustomRegionEnabled(),
                    homepage_and_discovery: isModularHomeExperienceEnabled(),
                    email_customizations:
                        isComsEnabled() && isCustomizeEmailFromAdminEnabled(),
                    variables:
                        hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                        isVariablesUIEnabled() &&
                        isOrgUiEnabled() &&
                        getCurrentOrgId() === -1,
                },
            },
            __typename: 'Admin__Data' as const,
        };
    } 
        return {
            status: {
                user_management: { users: true, groups: true, roles: true },
                teams: { pricing: true },
                application_settings: {
                    chart_customization:
                        isCustomChartEnabled() || isCustomRegionEnabled(),
                    homepage_and_discovery: isModularHomeExperienceEnabled(),
                    email_customizations:
                        isComsEnabled() && isCustomizeEmailFromAdminEnabled(),
                    variables:
                        hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                        isVariablesUIEnabled() &&
                        isOrgUiEnabled() &&
                        getCurrentOrgId() === -1,
                },
            },
            __typename: 'Admin__Data' as const,
        } as unknown as AdminStatusData;

}

export interface AdminItemMeta {
    commandId: string;
    sectionId?: string;
    orgContext?: string;
}

/** SpotlightItem extended with admin navigation metadata. */
export type AdminSpotlightItem = SpotlightItem & { meta: AdminItemMeta };

function toSpotlightItems(commands: AdminCommand[], contextSuffix?: string, contextLabel?: string): AdminSpotlightItem[] {
    // Section commands without a parentId are not navigable — exclude them.
    return commands
        .filter(cmd => !(cmd.type === 'section' && !cmd.parentId))
        .map(cmd => {
            const parentLabel =
                cmd.type === 'section' && cmd.parentId
                    ? commands.find(c => c.type === 'page' && c.id === cmd.parentId)
                          ?.label
                    : undefined;

            const baseDescription = joinPath(cmd.section, parentLabel);
            const description = joinPath(contextLabel, baseDescription);

            let orgContext: string | undefined;
            if (contextSuffix === 'primary') {
                orgContext = 'primary-org';
            } else if (contextSuffix === 'all-org') {
                orgContext = 'all-org';
            }

            const meta: AdminItemMeta = {
                commandId: cmd.type === 'section' ? cmd.parentId : cmd.id,
                sectionId: cmd.sectionId,
                orgContext,
            };

            return {
                id: `${ADMIN_ITEM_PREFIX}${cmd.id}${contextSuffix ? `-${contextSuffix}` : ''}`,
                label: cmd.label,
                description,
                icon: IconID.COG,
                type: SpotlightItemType.ADMIN,
                keywords: cmd.searchText.split(/\s+/).filter(Boolean),
                meta,
            };
        });
}

export function isAdminSpotlightItem(item: SpotlightItem): boolean {
    return item.type === SpotlightItemType.ADMIN;
}

function buildAdminCommands(
    apiData: AdminStatusData,
    t: TranslateFn,
    isAllOrgContext?: boolean,
): AdminCommand[] {
    const orgId = getCurrentOrgId();
    const resolvedIsAllOrgContext = isAllOrgContext ?? orgId === -1;
    const V1_SEGMENT = isReactshellEnabled() ? '/v1' : '';

    let tabsConfig: AdminStatusData = apiData;
    if (isOrgsOnFTEnabled()) {
        tabsConfig = {
            // eslint-disable-next-line no-underscore-dangle
            __typename: apiData.__typename,
            status: {
                ...apiData.status,
                teams: { pricing: true },
            },
        } as unknown as AdminStatusData;
    }

    const sideBarConfig = getSideBarConfig(
        t,
        tabsConfig,
        isOktaEnabled(),
        false,
        resolvedIsAllOrgContext,
        isCopilotEnabled(),
        V1_SEGMENT,
    );

    return buildCommandRegistry(sideBarConfig);
}


/**
 * Hook that eagerly fetches admin sidebar config via Admin_getStatus
 * GraphQL query, builds the command registry, and converts to
 * SpotlightItem[] — so admin items appear in Spotlight immediately
 * without requiring a visit to /admin first.
 */
export function useAdminSpotlightItems(navService?: {
    goToUrl?: (url: string) => void;
}) {
    const { t } = useTranslation();
    const [primaryCmds, setPrimaryCmds] = useState<AdminCommand[]>([]);
    const [allOrgsCmds, setAllOrgsCmds] = useState<AdminCommand[]>([]);
    const [sageFeatureInAllOrgs, setSageFeatureInAllOrgs] = useState(false);
    const [administrationInAllOrgs, setAdministrationInAllOrgs] = useState(false);
    const [searchSpotIQInAllOrgs, setSearchSpotIQInAllOrgs] = useState(false);
    const { isAuthenticated, authenticationChangeInProgress } =
        useAuthenticatedState();

    const { getStatus: fetchStatus, data, error } = useGetStatusLazy();
    const {
        getStatus: fetchStatusAlt,
        data: dataAlt,
        error: errorAlt,
    } = useGetStatusLazy();
    

    useEffect(() => {
        if (authenticationChangeInProgress) return;
        if (!isAuthenticated) {
            setPrimaryCmds([]);
            setAllOrgsCmds([]);
            return;
        }
        if (!isAdminTabVisible()) return;

        const orgId = getCurrentOrgId();
        const orgsEnabled =
            isOrgUiEnabled() && (orgId === -1 || orgId === 0);
        let viewMode = '';
        if (orgId === -1) {
            viewMode = 'all';
        } else if (orgId === 0) {
            viewMode = 'primary';
        }
        fetchStatus({ variables: { viewMode, orgsEnabled } });

        // When in a multi-org context, also fetch the alternate org context
        // so both All Orgs and Primary Org tabs appear in Spotlight on load.
        if (orgsEnabled && (orgId === -1 || orgId === 0)) {
            const altViewMode = orgId === -1 ? 'primary' : 'all';
            // Do not pass orgsEnabled for the all-orgs fetch — matches sidebar tab-switch behavior
            if (altViewMode === 'all') {
                fetchStatusAlt({ variables: { viewMode: altViewMode } });
            } else {
                fetchStatusAlt({ variables: { viewMode: altViewMode, orgsEnabled } });
            }
        }
    }, [isAuthenticated, authenticationChangeInProgress]);

    useEffect(() => {
        if (!isAdminTabVisible()) return;

        const orgId = getCurrentOrgId();
        const orgsEnabled = isOrgUiEnabled() && (orgId === -1 || orgId === 0);
        const primaryIsAllOrg = orgId === -1;

        const primaryData = data?.Data;
        const altData = dataAlt?.Data;

        if (primaryData || altData) {
            try {
                const primaryCommands = primaryData
                    ? buildAdminCommands(primaryData, t, primaryIsAllOrg)
                    : [];
                const altCommands = altData
                    ? buildAdminCommands(altData, t, !primaryIsAllOrg)
                    : [];
                setPrimaryCmds(primaryCommands);
                setAllOrgsCmds(altCommands);

                // sage_feature and administration are only under All Orgs context
                const allOrgData = primaryIsAllOrg ? primaryData : altData;
                const allOrgAppSettings = (allOrgData?.status
                    ?.application_settings as any) ?? {};
                setSageFeatureInAllOrgs(!!allOrgAppSettings.sage_feature);
                setAdministrationInAllOrgs(!!allOrgAppSettings.administration);
                setSearchSpotIQInAllOrgs(!!allOrgAppSettings.search);
            } catch {
                // Fall through to default config below
            }
        } else if (error || (data && !data?.Data)) {
            if (!isSaaSCluster()) {
                try {
                    if (orgsEnabled) {
                        const allOrgCommands = buildAdminCommands(
                            getDefaultStatusConfig(CONTEXT_ALL_ID),
                            t,
                            true,
                        );
                        const primaryCommands = buildAdminCommands(
                            getDefaultStatusConfig(CONTEXT_PRIMARY_ID),
                            t,
                            false,
                        );
                        setPrimaryCmds(primaryCommands);
                        setAllOrgsCmds(allOrgCommands);
                    } else {
                        setPrimaryCmds(
                            buildAdminCommands(getDefaultStatusConfig(), t),
                        );
                        setAllOrgsCmds([]);
                    }
                } catch {
                    // Silently ignore config building errors
                }
            }
        }
    }, [data, error, dataAlt, errorAlt, t]);


    const adminSpotlightItems = useMemo(() => {
        const hasBothOrgContexts =
            primaryCmds.length > 0 && allOrgsCmds.length > 0;
        const allCmds = [...primaryCmds, ...allOrgsCmds];

        const buildContextualSubItems = (
            builder: (t: TranslateFn, contextLabel: string | undefined, orgContext: string | undefined) => SpotlightItem[],
            primaryCmdsSet: AdminCommand[],
            allOrgsCmdsSet: AdminCommand[],
            cmdId: string,
        ): SpotlightItem[] => {
            const items: SpotlightItem[] = [];
            if (hasBothOrgContexts) {
                if (hasCommandWithId(primaryCmdsSet, cmdId)) {
                    items.push(...builder(t, t('orgs.adminPanel.primaryorg'), CONTEXT_PRIMARY_ID));
                }
                if (hasCommandWithId(allOrgsCmdsSet, cmdId)) {
                    items.push(...builder(t, t('orgs.adminPanel.allorg'), CONTEXT_ALL_ID));
                }
            } else if (hasCommandWithId(allCmds, cmdId)) {
                items.push(...builder(t, undefined, undefined));
            }
            return items;
        };

        const allOrgLabel = t('orgs.adminPanel.allorg');

        // ThoughtSpot AI — only All Orgs, gated via raw status (not in getSideBarConfig)
        const thoughtspotAIItems = sageFeatureInAllOrgs
            ? buildThoughtSpotAIItems(t, allOrgLabel, CONTEXT_ALL_ID)
            : [];

        // Administration — only All Orgs, gated via raw status
        const administrationItems = administrationInAllOrgs
            ? buildAdministrationItems(t, allOrgLabel, CONTEXT_ALL_ID)
            : [];

        // Search & SpotIQ — only All Orgs, gated via raw status
        const searchSpotIQItems = searchSpotIQInAllOrgs
            ? buildSearchSpotIQItems(t, allOrgLabel, CONTEXT_ALL_ID)
            : [];

        const extraItems = [
            ...buildContextualSubItems(buildStyleCustomizationItems, primaryCmds, allOrgsCmds, STYLE_CUSTOMIZATION_CMD_ID),
            ...buildContextualSubItems(buildHomepageDiscoveryItems, primaryCmds, allOrgsCmds, HOMEPAGE_DISCOVERY_CMD_ID),
            ...buildContextualSubItems(buildChartCustomizationItems, primaryCmds, allOrgsCmds, CHART_CUSTOMIZATION_CMD_ID),
            ...thoughtspotAIItems,
            ...administrationItems,
            ...searchSpotIQItems,
        ];

        if (hasBothOrgContexts) {
            return [
                ...toSpotlightItems(primaryCmds, 'primary', t('orgs.adminPanel.primaryorg')),
                ...toSpotlightItems(allOrgsCmds, 'all-org', t('orgs.adminPanel.allorg')),
                ...extraItems,
            ];
        }
        return [...toSpotlightItems(allCmds), ...extraItems];
    }, [primaryCmds, allOrgsCmds, sageFeatureInAllOrgs, administrationInAllOrgs, searchSpotIQInAllOrgs, t]);

    const handleAdminItemSelect = useCallback(
        (item: SpotlightItem) => {
            const { meta } = item as AdminSpotlightItem;
            if (!meta?.commandId) return;

            // Persist target tab and org context so the admin sidebar picks them up on mount
            // (handles the case where admin MFE hasn't loaded yet).
            localStorage.setItem('AdminLandingTabId', meta.commandId);
            if (meta.orgContext) {
                localStorage.setItem('AdminLandingOrgContext', meta.orgContext);
            }

            navService?.goToUrl?.('/admin');

            // Dispatch synchronously: if admin sidebar is already mounted its listener
            // handles this immediately; if not yet mounted, the localStorage values above
            // are read on mount and cover that case.
            window.dispatchEvent(
                new CustomEvent('admin:command-palette-navigate', {
                    detail: {
                        commandId: meta.commandId,
                        sectionId: meta.sectionId,
                        orgContext: meta.orgContext,
                    },
                }),
            );
        },
        [navService],
    );

    return { adminSpotlightItems, handleAdminItemSelect };
}
